import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Anchor } from './Anchor'

export type LinkKind = 'nav' | 'footer' | 'util'

/**
 * Exported so NavBar can style its dropdown-trigger <button>s and drawer
 * labels identically to real links.
 */
export const linkVariants = cva('no-underline transition-colors duration-150', {
  variants: {
    kind: {
      nav: 'text-ink-600 hover:text-ink active:text-brand-800 font-medium',
      footer: 'text-ink-500 hover:text-ink active:text-brand-800',
      // Color for `util` is set via compoundVariants (see `emphasis`) so it stays ink in the
      // nav but can opt into brand green in content — no competing text-color classes.
      util: 'inline-flex items-center gap-2 font-semibold whitespace-nowrap',
    },
    /** Text + leading-icon size. Icon scales with the label. */
    size: {
      md: 'text-sm [&_svg]:size-4',
      sm: 'text-xs [&_svg]:size-3.5',
    },
    /**
     * `util` only: brand-green resting color for a content utility link that must read as
     * clickable on its own (e.g. a document link inside a card). Leave off in the nav.
     */
    emphasis: {
      true: '',
      false: '',
    },
    onDark: {
      true: 'text-white/74 hover:text-white active:text-brand-300',
      false: '',
    },
    /** Current-page underline, 8px below the text. */
    active: {
      true: "relative after:absolute after:inset-x-0 after:-bottom-2 after:h-px after:bg-white/60 after:content-['']",
      false: '',
    },
  },
  compoundVariants: [
    // Light-theme util color: ink at rest, green on hover (default) — unchanged nav/footer-cluster look.
    {
      kind: 'util',
      emphasis: false,
      onDark: false,
      className: 'text-ink-600 hover:text-brand-800 active:text-brand-800',
    },
    // Light-theme util with emphasis: brand green at rest.
    {
      kind: 'util',
      emphasis: true,
      onDark: false,
      className: 'text-brand-800 hover:text-brand-900 active:text-brand-900',
    },
    { kind: 'util', onDark: true, className: 'hover:text-brand-300' },
  ],
  defaultVariants: { kind: 'nav', size: 'md', emphasis: false, onDark: false, active: false },
})

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof linkVariants> {
  /** Leading icon for the "util" kind (client login, phone). Pass a Lucide icon element — sized to 16px here. */
  icon?: React.ReactNode
}

/**
 * Text link. No underline by default; weight/color shift on hover, settles
 * into brand color on click.
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      kind = 'nav',
      size = 'md',
      emphasis = false,
      icon,
      onDark = false,
      active = false,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    return (
      <Anchor
        ref={ref}
        className={cn(linkVariants({ kind, size, emphasis, onDark, active }), className)}
        {...rest}
      >
        {icon}
        {children}
      </Anchor>
    )
  }
)
Link.displayName = 'Link'
